<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Menu from 'primevue/menu';
import Button from 'primevue/button';
import Avatar from 'primevue/avatar';
import useUserStore from '../stores/useUserStore';
import { useRouter, useRoute } from 'vue-router';
import { getLocaleFromPath, addLocaleToPath } from '../i18n/index.js';

const { t } = useI18n();

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const menu = ref();

const userProfile = computed(() => userStore.userProfile);
const isLoading = computed(() => userStore.isLoading);
const userInitial = computed(() => userStore.userInitial);

// Get current locale for proper navigation
const currentLocale = computed(() => getLocaleFromPath(route.path));

const handleLogout = async () => {
  try {
    await userStore.logout();
    // Use locale-aware navigation for logout redirect
    const loginPath = addLocaleToPath('/login', currentLocale.value);
    router.push(loginPath);
  } catch (error) {
    console.error('Logout failed:', error);
    // Handle error appropriately
  }
};

const menuItems = computed(() => [
  {
    label: t('user_menu_logout'),
    icon: 'pi pi-power-off',
    command: handleLogout
  }
]);

onMounted(async () => {
  if (!userStore.initializationComplete) {
    await userStore.initializeAuth();
  }
});
</script>

<template>
  <div class="user-menu">
    <Button @click="menu.toggle($event)" class="p-button-text user-menu-button">
      <div class="flex items-center gap-2">
        <Avatar 
          v-if="!isLoading"
          :image="userProfile?.photoURL" 
          :label="userInitial"
          shape="circle" 
          size="normal"
          class="user-avatar" 
        />
        <span v-if="!isLoading" class="font-medium hidden md:inline text-gray-700">
          {{ userProfile?.name || t('user_menu_guest') }}
        </span>
        <span v-else class="loading-placeholder"></span>
      </div>
    </Button>
    <Menu style="z-index: 40 !important;" ref="menu" :model="menuItems" :popup="true" />
  </div>
</template>

<style scoped>
.user-menu {
  display: flex;
  align-items: center;
}

.user-menu-button {
  padding: 0.5rem !important;
  background: transparent !important;
}

.user-menu-button:hover {
  background: var(--surface-100) !important;
}

.user-avatar {
  width: 2.5rem;
  height: 2.5rem;
}

.loading-placeholder {
  width: 100px;
  height: 1rem;
  background: var(--surface-200);
  border-radius: 4px;
  animation: pulse 1.5s infinite;
}

.menu-container {
  z-index: 40 !important;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}
</style>
